/**
 * Components inspector runtime.
 *
 * Injected into the host page (alongside the timeline runtime) when the
 * DevTools panel is enabled. Answers the panel's tree/details requests over the
 * Vite HMR channel, keeps a watched element's details fresh as it updates, and
 * outlines elements the panel hovers in its tree.
 */

import {createPageScriptChannel} from 'devframe/in-page-channel';
import {chromeTracksSupported} from '../timeline/chrome-tracks.js';
import {defineFramesById} from '../define-sites.js';
import {elementById, idOf} from '../timeline/identity.js';
import {withDevtoolsCause} from '../timeline/cause-context.js';
import {findByKey, keyOf} from './element-key.js';
import {pageChannel} from '../page-channel.js';
import {PAGE_ID} from '../page-id.js';
import {
  onRuntimeJoined,
  runtimesOnPage,
  yieldsToAnotherRuntime,
} from '../runtime-registry.js';
import type {ViteHotLike} from '../page-channel.js';
import {
  buildTree,
  collectDetails,
  collectOverrides,
  expandPath,
  listedIdOf,
  setProp,
  valueAt,
} from './collect.js';
import {
  clearLeafOverrides,
  persistLeafOverride,
  reapplyLeaves,
  removeLeafOverride,
  sweepLeaves,
  toggleLeafOverride,
} from './leaf-overrides.js';
import {
  applyOverrides,
  clearOverrides,
  onIntrinsicWrite,
  persistOverride,
  removeOverride,
  toggleOverride,
} from './prop-overrides.js';
import {forgetWaiting, onRestored, saveOverrides} from './override-store.js';
import {anatomyById, clearAnatomy, focusAnatomy} from './anatomy-overlay.js';
import {shadowOf} from './anatomy.js';
import {onProviderChange} from './extras.js';
import {onInternalsChange} from '../element-internals.js';
import {onLitWarning} from '../timeline/lit-warnings.js';
import {clearBreakpoints, setBreakpoint} from './breakpoints.js';
import {
  clearHighlight,
  highlightAll,
  highlightById,
  revealById,
} from './highlight.js';
import {
  LIT_IN_PAGE_CHANNEL,
  type LitInPageProtocol,
} from '../../../types/in-page.js';
import {
  INSPECT_CMD_CHANNEL,
  BREAKPOINTS_KEY,
  ELEMENT_BY_ID_KEY,
  LIT_ID_OF_KEY,
  INSPECT_DATA_CHANNEL,
  type GeneratedFrame,
  type InspectorCommand,
  type InspectorDetails,
  type PageBreakpoints,
  type InspectorMessage,
  type InspectorTreeNode,
  type LitPackageVersions,
  type ValueSegment,
} from '../../../types/inspector.js';
import {CHANNEL_RECORDING_CHANGED} from '../../../types/timeline.js';

/** Lit packages and the global each pushes its version onto when loaded. */
const LIT_VERSION_GLOBALS = [
  ['lit-html', 'litHtmlVersions'],
  ['lit-element', 'litElementVersions'],
  ['@lit/reactive-element', 'reactiveElementVersions'],
] as const;

const hot = (import.meta as {hot?: ViteHotLike}).hot;
if (hot !== undefined) pageChannel.useViteHot(hot);
// On a page another runtime serves, its inspector answers this carrier too.
const installs = typeof window !== 'undefined' && !yieldsToAnotherRuntime(hot);

// The direct panel channel, outside the `hot` gate on purpose: it is the one
// part of the inspector that does not need a dev server, which is what lets
// the hover outline keep working in a static snapshot of the panel. Answering
// handshakes costs nothing until a panel actually sends one.

// Set by the inspector block below, which owns the watch hook and the
// observer. The channel is created first, outside it, so it reaches them
// through these.
let onPanelsGone: (() => void) | undefined;
let onPanelArrived: (() => void) | undefined;
// Which panels are connected right now. A reloaded panel connects before its
// predecessor's heartbeat times out, so a release has to wait for the *last*
// one rather than react to the first disconnect.
const panels = new Set<string>();

/**
 * Take down what the panels left on the page once none is left. Their own
 * off-switches are RPCs a closing panel fires and a closing frame drops, so
 * the page does it itself.
 */
const releasePage = (): void => {
  clearHighlight();
  clearAnatomy();
  clearBreakpoints();
  try {
    onPanelsGone?.();
  } catch {
    // Never throw into the app.
  }
};

/** The console name of the panel's selection, as `$0` is the Elements panel's. */
const SELECTION_GLOBAL = '$lit';
/** Set on the global once `$lit` is ours, so a re-run runtime may replace it. */
const SELECTION_OWNED = Symbol.for('@oddsquad/vite-plugin-lit#selection');

/**
 * Define `$lit` in the page's console: the element the panel has selected,
 * or undefined while none is. A page that has a `$lit` of its own keeps it.
 */
const exposeSelection = (selected: () => Element | undefined): void => {
  const g = globalThis as unknown as Record<symbol, unknown>;
  if (SELECTION_GLOBAL in globalThis && g[SELECTION_OWNED] !== true) return;
  try {
    Object.defineProperty(globalThis, SELECTION_GLOBAL, {
      get: selected,
      configurable: true,
    });
    g[SELECTION_OWNED] = true;
  } catch {
    // A frozen global; the console goes without `$lit`.
  }
};

if (installs) {
  // The browser extension's "Reveal in Elements": DevTools' `inspectedWindow
  // .eval` runs in the page's main world and holds only the panel's numeric
  // id, so it turns it back into the node through this to call `inspect()`.
  (globalThis as unknown as Record<symbol, unknown>)[ELEMENT_BY_ID_KEY] =
    elementById;
  // And back: the node DevTools has selected (`$0`) to the component the
  // panel should select, for the extension's Elements-to-Lit sync.
  (globalThis as unknown as Record<symbol, unknown>)[LIT_ID_OF_KEY] =
    listedIdOf;
  // The extension's breakpoints, set from DevTools by the same route.
  const breakpoints: PageBreakpoints = {
    set: (id, target, on) => {
      const el = elementById(id);
      return el !== undefined && setBreakpoint(el, target, on);
    },
    clear: clearBreakpoints,
  };
  (globalThis as unknown as Record<symbol, unknown>)[BREAKPOINTS_KEY] =
    breakpoints;
  const channel = createPageScriptChannel<LitInPageProtocol>({
    name: LIT_IN_PAGE_CHANNEL,
    functions: {},
    events: {
      highlight: {handler: (id) => highlightById(id)},
      highlightAll: {handler: (ids) => void highlightAll(ids)},
      reveal: {handler: (id) => revealById(id)},
      anatomy: {handler: (id, only) => anatomyById(id, only)},
      anatomyFocus: {handler: (focus) => focusAnatomy(focus)},
    },
  });
  channel.events.on('panel:connected', (panel) => {
    panels.add(panel.id);
    try {
      onPanelArrived?.();
    } catch {
      // Never throw into the app; the panel can re-request.
    }
  });
  // A panel that goes away mid-hover never gets to send its `highlight(null)`,
  // and an outline left painted over the app is worse than a missing one.
  channel.events.on('panel:disconnected', (panel) => {
    clearHighlight();
    panels.delete(panel.id);
    if (panels.size === 0) releasePage();
  });
}

if (installs) {
  /**
   * Reads one of the page's own scripts for a host that cannot (the
   * standalone server). Only http(s) URLs on this page's origin are fetched:
   * all the host can make the page do is read its own files, with its own
   * cookies, and the server never fetches a page's URL itself. Anything else,
   * and any failure, answers `ok: false`.
   */
  const fetchText = async (url: string): Promise<InspectorMessage> => {
    try {
      const target = new URL(url, location.href);
      if (
        (target.protocol !== 'http:' && target.protocol !== 'https:') ||
        target.origin !== location.origin
      ) {
        return {type: 'fetched-text', url, ok: false};
      }
      const res = await fetch(target.href);
      if (!res.ok) return {type: 'fetched-text', url, ok: false};
      const sourceMap =
        res.headers.get('SourceMap') ?? res.headers.get('X-SourceMap');
      return {
        type: 'fetched-text',
        url,
        ok: true,
        text: await res.text(),
        ...(sourceMap === null ? {} : {sourceMap}),
      };
    } catch {
      return {type: 'fetched-text', url, ok: false};
    }
  };

  const send = (msg: InspectorMessage): void => {
    try {
      pageChannel.send(INSPECT_DATA_CHANNEL, {...msg, pageId: PAGE_ID});
    } catch {
      // HMR channel temporarily unavailable; the panel can re-request.
    }
  };

  /**
   * What the page can say about itself, for the panel's empty state. Reads
   * the version sentinels each Lit package pushes once per loaded copy, so
   * more than one entry means duplicate copies. Never throws.
   */
  const readyMessage = (): InspectorMessage => {
    const g = globalThis as Record<string, unknown>;
    const litPackages: LitPackageVersions = {};
    for (const [name, global] of LIT_VERSION_GLOBALS) {
      const list = g[global];
      if (!Array.isArray(list)) continue;
      const versions = list.filter((v): v is string => typeof v === 'string');
      if (versions.length > 0) litPackages[name] = versions;
    }
    let topFrame = true;
    try {
      topFrame = window.top === window;
    } catch {
      // Reading a cross-origin `top` can throw; that is a frame.
      topFrame = false;
    }
    return {
      type: 'ready',
      litPackages,
      topFrame,
      chromeTracks: chromeTracksSupported(),
      runtimes: [...runtimesOnPage()],
    };
  };

  // -------------------------------------------------------------------------
  // Live-watch: re-push details whenever the watched element finishes updating
  // or its slot assignments change.
  // We wrap the instance's `updated` (not the prototype) so the hook is scoped
  // to one element and trivially removable, and it works without the timeline's
  // recording gate.
  // -------------------------------------------------------------------------

  type Updatable = Element & {
    updated?: (changed: unknown) => void;
  };
  // Hold the watched element via a WeakRef so an active watch doesn't keep a
  // removed element alive: the override and `restore` only reach `el` through
  // the ref, leaving the DOM as its sole strong root.
  let watched: {
    ref: WeakRef<Updatable>;
    restore: () => void;
    /** Push fresh details once, at the end of the current task. */
    queuePush: () => void;
  } | null = null;

  exposeSelection(() => watched?.ref.deref());

  // An app write under an override renders nothing, so `updated` never
  // hears of it; the intrinsic value the pane shows would go stale.
  onIntrinsicWrite((el) => {
    if (watched !== null && watched.ref.deref() === el) watched.queuePush();
  });

  const unwatch = (): void => {
    const prev = watched;
    watched = null;
    prev?.restore();
  };

  const watch = (id: number): void => {
    unwatch();
    const el = elementById(id) as Updatable | undefined;
    if (el === undefined) {
      send({type: 'gone', id});
      return;
    }
    const hadOwn = Object.prototype.hasOwnProperty.call(el, 'updated');
    // Only an *own* `updated` is stable across an HMR hot patch. A prototype
    // implementation is replaced in place on every patch (`syncOwnMembers` in
    // ../patch.ts), so caching it here would pin the watched instance to the
    // pre-edit code for the life of the watch — resolve it through the
    // prototype chain on each call instead. Our wrapper is an own property, so
    // the lookup skips it and cannot recurse.
    const ownPrev = hadOwn ? el.updated : undefined;
    const ref = new WeakRef(el);
    /** The details last sent, for {@link onSlotChange} to compare with. */
    let lastSent: InspectorDetails | undefined;
    const push = (onlyIfChanged = false): void => {
      // Skip if the watch ended or moved on in the meantime.
      if (watched?.ref !== ref) return;
      const cur = ref.deref();
      if (cur !== undefined) {
        const details = collectDetails(cur);
        // Serialized only on this path: slot and child changes are rare,
        // updates are not.
        if (
          !onlyIfChanged ||
          JSON.stringify(details) !== JSON.stringify(lastSent)
        ) {
          lastSent = details;
          send({type: 'details', details});
        }
        // The app may have replaced an object a leaf override was in.
        if (sweepLeaves(cur)) replyOverrides();
      }
    };
    // Moving a light child between slots, or adding or removing one, does
    // not update the element, so its slots' `slotchange` re-pushes details
    // too. The event bubbles to the shadow root; one listener there covers
    // every slot, including ones a later render adds. Several slots usually
    // change together, so they share one push.
    // The children observer below hears a slot move too, a microtask after
    // `slotchange`, so a queued push that would repeat the last one is
    // dropped.
    let pushQueued = false;
    const onSlotChange = (): void => {
      if (pushQueued) return;
      pushQueued = true;
      queueMicrotask(() => {
        pushQueued = false;
        push(true);
      });
    };
    // The shadow root can appear after the first render, so `updated` retries.
    // Re-adding the same listener to the same root is a no-op.
    const listen = (target: Element): void => {
      shadowOf(target)?.addEventListener('slotchange', onSlotChange);
    };
    listen(el);
    // A provider's raw `setValue` does not update its host; re-wrapped on each
    // update, which may have added a provider or hot-patched one.
    let releaseProviders = onProviderChange(el, onSlotChange);
    // Custom states and the form value change through the element's
    // internals, often without an update (a focus handler adding a state).
    const releaseInternals = onInternalsChange(el, onSlotChange);
    // A light child whose slot name no slot has is assigned nowhere, so its
    // arrival, removal or renaming fires no `slotchange`: the Anatomy list of
    // content that isn't rendered would go stale. Light children only.
    const children = new MutationObserver((records) => {
      const direct = (r: MutationRecord): boolean =>
        r.type === 'childList' ? r.target === el : r.target.parentNode === el;
      if (records.some(direct)) onSlotChange();
    });
    children.observe(el, {
      childList: true,
      subtree: true,
      attributeFilter: ['slot'],
    });
    el.updated = function (this: Updatable, changed: unknown) {
      const current =
        ownPrev ?? (Object.getPrototypeOf(this) as Updatable | null)?.updated;
      current?.call(this, changed);
      // Deferred: this runs inside performUpdate, which a SignalWatcher wraps
      // in a Computed, so reading a signal here would subscribe the element's
      // render to it. A microtask runs outside any tracking context.
      queueMicrotask(() => {
        const cur = ref.deref();
        if (cur !== undefined && watched?.ref === ref) {
          listen(cur);
          releaseProviders();
          releaseProviders = onProviderChange(cur, onSlotChange);
        }
        push();
      });
    };
    watched = {
      ref,
      queuePush: onSlotChange,
      restore: () => {
        children.disconnect();
        releaseProviders();
        releaseInternals();
        const cur = ref.deref();
        if (cur === undefined) return;
        shadowOf(cur)?.removeEventListener('slotchange', onSlotChange);
        if (hadOwn) cur.updated = ownPrev;
        else delete cur.updated;
      },
    };
    // Push an immediate snapshot so the panel doesn't wait for the next update.
    lastSent = collectDetails(el);
    send({type: 'details', details: lastSent});
  };

  // -------------------------------------------------------------------------
  // Live tree: while a panel is open, watch the DOM (light + every shadow
  // root) and push a fresh tree when the component hierarchy actually
  // changes. childList-only (no attributes), so cosmetic changes — including
  // our own highlight box toggling — don't churn; identical rebuilds are also
  // deduped. Batches that only move text or comment nodes (most Lit
  // re-renders) are dropped before they schedule anything, and only added
  // subtrees are walked for new shadow roots, so a busy page no longer costs
  // a full-page walk per batch.
  //
  // Rebuilds are paced by their own cost: the next one waits at least
  // {@link PACE_FACTOR} times as long as the last took (and never less than
  // {@link MIN_PACE_MS}), so a page whose tree is slow to walk spends a
  // bounded share of its main thread on the panel. While the Timeline
  // records, none run at all, so they stay out of what it measures; the
  // changes they missed land in one rebuild when it stops.
  // -------------------------------------------------------------------------

  /** The shortest wait between two rebuilds, in ms. */
  const MIN_PACE_MS = 100;
  /** How many times the last rebuild's cost the next one waits. */
  const PACE_FACTOR = 5;

  let observer: MutationObserver | null = null;
  let observeTimer: ReturnType<typeof setTimeout> | undefined;
  let lastTreeJson = '';
  /** What the last rebuild cost, in ms; sets the wait before the next. */
  let lastBuildMs = 0;
  /** Whether the Timeline is recording; rebuilds wait it out. */
  let recording = false;
  /** A change arrived while recording, so one rebuild is owed at the end. */
  let heldWhileRecording = false;
  // Added elements whose subtrees still need their shadow roots observed.
  let pendingRoots: Element[] = [];

  /**
   * Observe every shadow root at or under `root` (and `root`'s subtree),
   * closed ones too where Lit keeps them on the element, as the tree does.
   */
  const observeShadowRoots = (
    obs: MutationObserver,
    root: ParentNode
  ): void => {
    const own = root instanceof Element ? shadowOf(root) : null;
    if (own !== null) {
      obs.observe(own, {childList: true, subtree: true});
      observeShadowRoots(obs, own);
    }
    for (const el of root.querySelectorAll('*')) {
      const shadow = shadowOf(el);
      if (shadow !== null) {
        obs.observe(shadow, {childList: true, subtree: true});
        observeShadowRoots(obs, shadow);
      }
    }
  };

  /**
   * Re-attach the observer to the document and every shadow root in it. The
   * document element rather than the body: a page that swaps in a new body
   * (Turbo Drive) leaves an observer of the old one hearing nothing.
   */
  const syncObserverTargets = (obs: MutationObserver): void => {
    obs.disconnect();
    obs.observe(document.documentElement, {childList: true, subtree: true});
    observeShadowRoots(obs, document);
  };

  // Tags already awaited, so a tag that stays undefined across many rebuilds
  // gets one listener.
  const awaiting = new Set<string>();

  /**
   * Build the tree, and for each undefined tag in it wait for its definition:
   * defining one upgrades its elements without touching the DOM, so the live
   * observer never hears of it and the row would stay flagged. Rows still
   * waiting to hydrate are waited on the same way ({@link awaitHydration}).
   */
  const build = (): InspectorTreeNode[] => {
    const roots = buildTree((tag) => {
      if (awaiting.has(tag)) return;
      awaiting.add(tag);
      void customElements.whenDefined(tag).then(
        () => {
          awaiting.delete(tag);
          scheduleRebuild();
        },
        () => awaiting.delete(tag)
      );
    });
    awaitHydration(roots);
    return roots;
  };

  /** Build the tree and its JSON, timing both for the pacing. */
  const timedBuild = (): {roots: InspectorTreeNode[]; json: string} => {
    const start = performance.now();
    const roots = build();
    const json = JSON.stringify(roots);
    lastBuildMs = performance.now() - start;
    return {roots, json};
  };

  const pushTreeIfChanged = (): void => {
    const {roots, json} = timedBuild();
    if (json === lastTreeJson) return;
    lastTreeJson = json;
    send({type: 'tree', roots});
  };

  // Elements already awaited, so a row rebuilt many times gets one wait.
  const hydrating = new WeakSet<Element>();

  /**
   * For each server-rendered row that has not hydrated yet, wait for its
   * first update: hydration reuses the server's nodes, and waking a deferred
   * element only removes an attribute, so the observer never hears of it
   * and the row (and the details, when selected) would keep the old state.
   */
  const awaitHydration = (nodes: readonly InspectorTreeNode[]): void => {
    for (const node of nodes) {
      awaitHydration(node.children);
      if (node.ssr !== 'deferred' && node.ssr !== 'pending') continue;
      const el = elementById(node.id) as
        | (Element & {updateComplete?: Promise<unknown>})
        | undefined;
      if (el === undefined || hydrating.has(el)) continue;
      if (el.updateComplete === undefined) continue;
      hydrating.add(el);
      void Promise.resolve(el.updateComplete).then(
        () => {
          hydrating.delete(el);
          if (watched?.ref.deref() === el) watched.queuePush();
          scheduleRebuild();
        },
        () => hydrating.delete(el)
      );
    }
  };

  // A warning lands after the element rendered, with no DOM change to tell
  // the observer.
  onLitWarning(() => scheduleRebuild());

  /** Whether a subtree holds anything the tree (or observer) cares about. */
  const mayHoldComponents = (node: Node): node is Element => {
    if (!(node instanceof Element)) return false;
    const matters = (el: Element): boolean =>
      el.localName.includes('-') || shadowOf(el) !== null;
    if (matters(node)) return true;
    for (const el of node.querySelectorAll('*')) if (matters(el)) return true;
    return false;
  };

  /** Queue the added nodes that may hold components; whether any did. */
  const queueAdded = (nodes: NodeList): boolean => {
    let any = false;
    for (const node of nodes) {
      if (mayHoldComponents(node)) {
        pendingRoots.push(node);
        any = true;
      }
    }
    return any;
  };

  const anyMayHold = (nodes: NodeList): boolean => {
    for (const node of nodes) if (mayHoldComponents(node)) return true;
    return false;
  };

  const flushMutations = (): void => {
    observeTimer = undefined;
    // Walk added subtrees now rather than on arrival: a custom element
    // attaches its shadow root when it upgrades, which can be after insert.
    // Removed roots need no bookkeeping; a detached shadow root just stops
    // producing records.
    const roots = pendingRoots;
    pendingRoots = [];
    if (observer !== null) {
      for (const root of roots) {
        if (root.isConnected) observeShadowRoots(observer, root);
      }
    }
    pushTreeIfChanged();
  };

  const onMutation = (records: MutationRecord[]): void => {
    // The tree only has custom elements in it, so a batch that adds or
    // removes none (text, comments, plain markup) can't change it.
    let relevant = false;
    for (const record of records) {
      if (queueAdded(record.addedNodes)) relevant = true;
      if (!relevant && anyMayHold(record.removedNodes)) relevant = true;
    }
    if (relevant) scheduleRebuild();
  };

  /**
   * Rebuild the tree once the pace allows. Changes that arrive while one is
   * queued ride along with it rather than push it back, so a page that never
   * stops changing still gets a fresh tree every so often. Nothing runs
   * without a panel watching, and nothing while the Timeline records.
   */
  const scheduleRebuild = (): void => {
    if (observer === null) return;
    if (recording) {
      heldWhileRecording = true;
      return;
    }
    if (observeTimer !== undefined) return;
    observeTimer = setTimeout(
      flushMutations,
      Math.max(MIN_PACE_MS, PACE_FACTOR * lastBuildMs)
    );
  };

  pageChannel.on(CHANNEL_RECORDING_CHANGED, (data) => {
    recording = (data as {recording: boolean}).recording;
    if (recording) {
      // A rebuild already queued would land mid-recording.
      if (observeTimer !== undefined) {
        clearTimeout(observeTimer);
        observeTimer = undefined;
        heldWhileRecording = true;
      }
    } else if (heldWhileRecording) {
      heldWhileRecording = false;
      scheduleRebuild();
    }
  });

  const setObserving = (enabled: boolean): void => {
    if (enabled) {
      if (observer === null) observer = new MutationObserver(onMutation);
      lastTreeJson = '';
      syncObserverTargets(observer);
      pushTreeIfChanged();
    } else if (observer !== null) {
      observer.disconnect();
      observer = null;
      pendingRoots = [];
      heldWhileRecording = false;
      if (observeTimer !== undefined) {
        clearTimeout(observeTimer);
        observeTimer = undefined;
      }
    }
  };

  // The panel is gone, so nobody is reading either stream. Drop the
  // MutationObserver (it rebuilds and stringifies the whole tree per batch)
  // and the instance-level `updated` wrapper. The node side keeps its tree
  // and details caches, and a returning panel re-arms both through `ready`.
  onPanelsGone = () => {
    try {
      unwatch();
    } finally {
      setObserving(false);
    }
  };
  // A panel that connects later cannot know whether this runtime was
  // released, so tell it the runtime is up; its `ready` handler re-requests
  // the tree and re-arms the selection and the live tree. Idempotent.
  onPanelArrived = () => send(readyMessage());

  // -------------------------------------------------------------------------
  // Command dispatch
  // -------------------------------------------------------------------------

  const replyTree = (): void => {
    const {roots, json} = timedBuild();
    lastTreeJson = json;
    send({type: 'tree', roots});
  };

  // Answers carry the element's key, so a panel can find it again after a
  // reload; a watch's pushes do not, as a stamped key walks the document.
  const replyDetails = (id: number): void => {
    const el = elementById(id);
    if (el === undefined) {
      send({type: 'gone', id});
      return;
    }
    send({type: 'details', details: {...collectDetails(el), key: keyOf(el)}});
  };

  const replyLocated = (
    cmd: Extract<InspectorCommand, {type: 'locate'}>
  ): void => {
    const el = findByKey(cmd.key);
    send({
      type: 'located',
      key: cmd.key,
      id: el === undefined ? null : idOf(el),
    });
  };

  const replyDefineFrames = (ids: number[]): void => {
    const frames: Record<number, GeneratedFrame[]> = {};
    for (const id of ids) {
      const known = defineFramesById(id);
      if (known !== undefined) frames[id] = known;
    }
    send({type: 'define-frames', frames});
  };

  const replyExpanded = (
    cmd: Extract<InspectorCommand, {type: 'expand'}>
  ): void => {
    const el = elementById(cmd.id);
    if (el === undefined) {
      send({type: 'gone', id: cmd.id});
      return;
    }
    const listed = expandPath(el, cmd.path);
    send({
      type: 'expanded',
      id: cmd.id,
      path: cmd.path,
      children: listed?.children ?? null,
      ...(listed !== null && listed.more > 0 ? {more: listed.more} : {}),
    });
  };

  const applySetProp = (
    cmd: Extract<InspectorCommand, {type: 'set-prop'}>
  ): void => {
    const el = elementById(cmd.id);
    if (el === undefined) {
      send({type: 'gone', id: cmd.id});
      return;
    }
    const keys = cmd.keys ?? [];
    const applied = setProp(el, cmd.name, cmd.value, keys);
    // Whether or not it applied, so a rejected value snaps back in the panel.
    replyDetails(cmd.id);
    replyLeafLevel(cmd.id, cmd.name, keys);
    if (applied) replyOverrides();
  };

  /**
   * Asks `cmd.id` to update; a watched element re-sends its details after.
   * The update's cause names the command, as for every command.
   */
  const requestUpdate = (
    cmd: Extract<InspectorCommand, {type: 'request-update'}>
  ): void => {
    const el = elementById(cmd.id) as
      | (Element & {requestUpdate?: () => void})
      | undefined;
    if (el === undefined) {
      send({type: 'gone', id: cmd.id});
      return;
    }
    el.requestUpdate?.();
  };

  /** Stores a row's value as the next free `tempN` and names it in the console. */
  const storeGlobal = (
    cmd: Extract<InspectorCommand, {type: 'store-global'}>
  ): void => {
    const el = elementById(cmd.id);
    if (el === undefined) {
      send({type: 'gone', id: cmd.id});
      return;
    }
    const at = valueAt(el, cmd.path);
    if (at === undefined) return;
    let n = 1;
    while (`temp${n}` in globalThis) n++;
    const name = `temp${n}`;
    (globalThis as Record<string, unknown>)[name] = at.value;
    console.log(name, at.value);
  };

  /**
   * A leaf deeper than the row's preview would leave the open level that
   * holds it stale, so answer that level too. Nothing for the property itself.
   */
  const replyLeafLevel = (
    id: number,
    name: string,
    keys: readonly ValueSegment[]
  ): void => {
    if (keys.length === 0) return;
    replyExpanded({
      type: 'expand',
      id,
      path: {section: 'prop', name, keys: keys.slice(0, -1)},
    });
  };

  // Every change to the overrides is answered with them, so this is also
  // where the ones kept across reloads are saved.
  const replyOverrides = (): void => {
    saveOverrides();
    send({type: 'overrides', ...collectOverrides()});
  };
  // Saved overrides go back on as their elements first update, with no
  // command to answer; several elements usually do so together.
  let restoreQueued = false;
  onRestored(() => {
    if (restoreQueued) return;
    restoreQueued = true;
    queueMicrotask(() => {
      restoreQueued = false;
      // Naming the restored elements mints their ids; walk the tree first
      // so ids still come in tree order, as the panel's selection expects
      // the same id for the same element after a reload.
      build();
      replyOverrides();
    });
  });

  /** Act on one element's override, then answer for it and for the list. */
  const onOverride =
    (act: (el: Element, name: string, keys: ValueSegment[]) => void) =>
    (cmd: {id: number; name: string; keys?: ValueSegment[]}): void => {
      const el = elementById(cmd.id);
      if (el === undefined) {
        send({type: 'gone', id: cmd.id});
        return;
      }
      const keys = cmd.keys ?? [];
      act(el, cmd.name, keys);
      replyDetails(cmd.id);
      replyLeafLevel(cmd.id, cmd.name, keys);
      replyOverrides();
    };

  // Commands the page does not act on (`pick`) have no entry.
  type CommandHandlers = {
    [K in InspectorCommand['type']]?: (
      cmd: Extract<InspectorCommand, {type: K}>
    ) => void;
  };

  const commandHandlers: CommandHandlers = {
    tree: replyTree,
    details: (cmd) => replyDetails(cmd.id),
    'define-frames': (cmd) => replyDefineFrames(cmd.ids),
    'fetch-text': (cmd) => void fetchText(cmd.url).then(send),
    expand: replyExpanded,
    'set-prop': applySetProp,
    overrides: replyOverrides,
    locate: replyLocated,
    'override-toggle': (cmd) =>
      onOverride((el, name, keys) =>
        keys.length === 0
          ? toggleOverride(el, name, cmd.enabled)
          : toggleLeafOverride(el, name, keys, cmd.enabled)
      )(cmd),
    'override-remove': onOverride((el, name, keys) =>
      keys.length === 0
        ? removeOverride(el, name)
        : removeLeafOverride(el, name, keys)
    ),
    'override-persist': (cmd) =>
      onOverride((el, name, keys) =>
        keys.length === 0
          ? persistOverride(el, name, cmd.persist)
          : persistLeafOverride(el, name, keys, cmd.persist)
      )(cmd),
    'overrides-apply': (cmd) => {
      applyOverrides(cmd.applied);
      reapplyLeaves();
      replyOverrides();
    },
    'overrides-clear': () => {
      clearOverrides();
      clearLeafOverrides();
      forgetWaiting();
      replyOverrides();
    },
    watch: (cmd) => (cmd.id === null ? unwatch() : watch(cmd.id)),
    // Fallback path. A panel that handshaked over the in-page channel emits
    // there instead and never reaches this.
    highlight: (cmd) => highlightById(cmd.id),
    // Fallback path, as for `highlight`.
    'highlight-all': (cmd) => highlightAll(cmd.ids),
    reveal: (cmd) => revealById(cmd.id),
    'request-update': requestUpdate,
    'store-global': storeGlobal,
    anatomy: (cmd) => anatomyById(cmd.id, cmd.only),
    'anatomy-focus': (cmd) => focusAnatomy(cmd.focus),
    observe: (cmd) => setObserving(cmd.enabled),
    release: () => {
      if (panels.size === 0) releasePage();
    },
  };

  pageChannel.on(INSPECT_CMD_CHANNEL, (data) => {
    const cmd = data as InspectorCommand;
    // Own keys only: a `type` such as `constructor` must not reach
    // Object.prototype and get called.
    if (!Object.hasOwn(commandHandlers, cmd.type)) return;
    const handler = commandHandlers[cmd.type] as
      | ((c: InspectorCommand) => void)
      | undefined;
    // An edit, an override or Request update schedules updates the timeline
    // should blame on the DevTools rather than leave without a cause.
    if (handler !== undefined) withDevtoolsCause(cmd.type, () => handler(cmd));
  });

  // Announce readiness so a panel that loaded first re-requests the tree.
  send(readyMessage());
  // A carrier attached later (a standalone dev server) starts with no idea a
  // runtime exists.
  pageChannel.onAttach(() => send(readyMessage()));
  // Another runtime arriving is news for the panel: they collide.
  onRuntimeJoined(() => send(readyMessage()));
}
